import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Form, Input, Radio, Alert, Bubble, Select, StatusTip, Button } from 'tea-component';
import './index.scss';
import { VIRTUAL_TABLE_NAME_REGEX } from '@dbl/core/global';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { showErrorMessagetip } from '@home/components/ErrorMessageTip/ErrorMessageTip';
import { addSqlViewLabel, getSqlViewLabel } from '@dbl/services/dataSource';

type SaveType = 'saveAs' | 'update';

interface VirtualTableEditFormProps {
  edit: boolean;
  isCreateNew: boolean;
  gameCode: string;
  style?: React.CSSProperties;
  dataTables?: any[];
  onFormValid: (valid: boolean) => void;
  currentStep?: string; // 用来更新下拉选渲染matchButtonWidth识别宽度
}

interface FormValues {
  name: string;
  saveType: SaveType;
  label?: string;
  cycle?: string;
  description: string;
}

export interface VirtualTableEditFormRef {
  getFormValues: () => FormValues;
  setNameRepeat: (value: string) => void;
}

const VirtualTableEditForm = forwardRef<VirtualTableEditFormRef, VirtualTableEditFormProps>(
  (props, ref) => {
    const { edit, isCreateNew, dataTables, gameCode, style, onFormValid, currentStep } = props;
    const { table_name: tableName, label: propsLabel, cycle: propsCycle, table_desc: tableDesc } = dataTables?.[0] || {};
    const { dblFormatMessage: multiFormatMessage, commonFormatMessage, formatMessage, isEn } = useDblIntl("virtual_table");
    const [name, setName] = useState(isCreateNew ? '' : `${tableName}(Copy)`);
    const [isNameChange, setIsNameChange] = useState(false); // 是否是用户手动修改了名称
    const [saveType, setSaveType] = useState<SaveType>(isCreateNew ? 'update' : 'saveAs');
    const [nameValid, setNameValid] = useState<string>('');
    const [translatorMap, setTranslatorMap] = useState({}); // 中英文翻译映射
    // 下拉选接口状态
    // label
    const [labelStatus, setLabelStatus] = useState<'' | 'loading' | 'empty' | 'error'>('');
    const [label, setLabel] = useState<string>(isCreateNew ? '' : propsLabel);
    const [showCustomLabelInput, setShowCustomLabelInput] = useState<boolean>(false);
    const [customLabelInputValue, setCustomLabelInputValue] = useState<string>('');
    const [labelOptions, setLabelOptions] = useState<any>([]);
    // cycle
    const [cycleStatus, setCycleStatus] = useState<'' | 'loading' | 'empty' | 'error'>('');
    const [cycle, setCycle] = useState<string>(isCreateNew ? '' : propsCycle);
    const [showCustomCycleInput, setShowCustomCycleInput] = useState<boolean>(false);
    const [customCycleInputValue, setCustomCycleInputValue] = useState<string>('');
    const [cycleOptions, setCycleOptions] = useState<any>([]);
    // description
    const [description, setDescription] = useState<string>(isCreateNew ? '' : tableDesc);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormValues: () => ({
        name: name.trim(),
        saveType,
        label,
        cycle,
        description: description.trim(),
      }),
      setNameRepeat: (value: string) => {
        setNameValid(value);
      },
    }));

    useEffect(() => {
      onFormValid(!nameValid && (!!name.trim()));
    }, [nameValid, name]);


    const handleNameChange = (value: string) => {
      const tempName = value?.trim();
      if (tempName?.length < 1) {
        setNameValid('dbl_common_cannot_be_empty');
      } else if (tempName?.length > 200) {
        setNameValid('dbl_virtual_table_source_name_length_limit');
      } else if (!VIRTUAL_TABLE_NAME_REGEX.test(tempName)) {
        setNameValid('dbl_virtual_table_source_name_valid');
      } else {
        setNameValid('');
      }
      setName(value);
      setIsNameChange(true);
    };

    const handleSaveTypeChange = (value: SaveType) => {

      if (value === 'saveAs' && !isNameChange) {
        // 在Save as的情况下，和原有虚拟表名相同, 自动加上Copy后缀, Update情况下与原有虚拟表名相同, 如果用户自己修改了, 以用户修改为准
        setName(`${tableName}(Copy)`);
      } else {
        // 未修改名称的情况下, 直接去掉Copy后缀
        const tempName = isNameChange ? name.trim() : name.trim().replace(/\(Copy\)$/, "");
        setName(tempName);
      }
      // 当报错信息是重名时, 切换save as 和 update时,要重置
      nameValid === 'dbl_virtual_table_source_name_repeat' && setNameValid('');
      setSaveType(value);
    };

    // 根据后端返回的中英映射, 获取文本
    const getText = (key: string, value: string): string => {
      const map = translatorMap?.[key]?.[value];
      return map?.[isEn ? 'en' : 'zh'] || value || "-";
    };

    const getOptions = (data: any[]) => data?.map((item: string) => ({
      value: item,
      text: item,
    }));

    // 获取下拉选列表数据
    const getLabeList = () => {
      setLabelStatus('loading');
      setCycleStatus('loading');
      const params = {
        game_code: gameCode,
        kind: "virtual"
      };
      getSqlViewLabel(params).then((res: any) => {
        const newLabelOptions = getOptions(res?.labels || []);
        const newCycleOptions = getOptions(res?.cycles || []);
        setTranslatorMap(res?.trans || {});
        setLabelOptions(newLabelOptions);
        setCycleOptions(newCycleOptions);
        if (newLabelOptions?.length === 0) {
          setLabelStatus('empty');
        } else {
          setLabelStatus('');
        }
        if (newCycleOptions?.length === 0) {
          setCycleStatus('empty');
        } else {
          setCycleStatus('');
        }
      }).catch((e) => {
        setLabelStatus('error');
        setCycleStatus('error');
      });
    };

    // 添加自定义标签
    const addLabeList = (value: string, type: string) => {
      const params = {
        game_code: gameCode,
        label: type === 'label' ? value : "",
        cycle: type === 'cycle' ? value : "",
        type,
      };
      addSqlViewLabel(params).then((res: any) => {
        const newLabelOptions = getOptions(res?.labels || []);
        const newCycleOptions = getOptions(res?.cycles || []);
        setLabelOptions(newLabelOptions);
        setCycleOptions(newCycleOptions);

        if (type === 'label') {
          setLabel(newLabelOptions?.[0]?.value ?? '');
          if (newLabelOptions?.length === 0) {
            setLabelStatus('empty');
          } else {
            setLabelStatus('');
          }
          setShowCustomLabelInput(false);
        }

        if (type === 'cycle') {
          setCycle(newCycleOptions?.[0]?.value ?? '');
          if (newCycleOptions?.length === 0) {
            setCycleStatus('empty');
          } else {
            setCycleStatus('');
          }
          setShowCustomCycleInput(false);
        }
      }).catch((e) => {
        if (e?.code === 21110) {
          showErrorMessagetip({
            errorCode: 21110,
            content: multiFormatMessage("task_label_same"),
            duration: 3000,
            isClose: true,
            type: 'error',
          });
        }
      });
    };

    // 初始化请求下拉选数据
    useEffect(() => {
      getLabeList();
    }, []);

    // 清空自定义输入框
    useEffect(() => {
      if (!showCustomLabelInput) {
        setCustomLabelInputValue('');
      }
      if (!showCustomCycleInput) {
        setCustomCycleInputValue('');
      }
    }, [showCustomLabelInput, showCustomCycleInput]);


    return (
      <Form className={'dbl-virtual-table-edit-form'} style={{ paddingTop: isCreateNew ? 20 : 11, ...style }}>
        {!isCreateNew && (
          <Form.Item
            label={multiFormatMessage('save_type')}
          >
            <Radio.Group
              value={saveType}
              onChange={(value) => handleSaveTypeChange(value as SaveType)}
            >
              <Bubble content={edit ? undefined : formatMessage({ id: 'dbl_common_no_permission' })}>
                <Radio disabled={!edit} name="update">
                  {multiFormatMessage('update')}
                </Radio>
              </Bubble>
              <Radio name="saveAs">
                {multiFormatMessage('save_as')}
              </Radio>
            </Radio.Group>
            {saveType === 'update' && (
              <Alert className={'dbl_virtual_table_update_warning'} type="warning">
                {multiFormatMessage('update_warning')}
              </Alert>
            )}
          </Form.Item>
        )}
        <Form.Item
          label={multiFormatMessage('source_name')}
          showStatusIcon={false}
          required
          status={!nameValid ? undefined : 'error'}
          message={
            nameValid && formatMessage({ id: nameValid })
          }
        >
          <Input
            className={'dbl-virtual-table-edit-form-input'}
            placeholder={multiFormatMessage('source_name_placeholder')}
            value={name}
            onChange={(value) => handleNameChange(value)}
          />
        </Form.Item>

        { /* label */}
        <Form.Item
          label={multiFormatMessage('label')}
          showStatusIcon={false}
          key={`${currentStep  }label`}
        >
          <Select
            className={'dbl-virtual-table-edit-form-input'}
            placeholder={multiFormatMessage('label_placeholder')}
            size="full"
            appearance="button"
            searchable={!showCustomLabelInput}
            matchButtonWidth
            value={label}
            onChange={(value) => {
              setLabel(value);
            }}
            options={!showCustomLabelInput ? labelOptions?.map(opt => ({ value: opt.value, text: getText('label', opt?.text) })) : []}
            boxClassName={showCustomLabelInput ? 'confirm-model-customize' : ''}
            tips={
              // eslint-disable-next-line no-nested-ternary
              showCustomLabelInput ? (false) : labelStatus && labelOptions.length === 0 ? (
                <StatusTip
                  status={labelStatus}
                  emptyText={commonFormatMessage("no_data")}
                  onRetry={() => {
                    getLabeList();
                  }}
                />
              ) : undefined
            }
            onOpen={() => {
              setShowCustomLabelInput(false);
            }}
            footer={
              !showCustomLabelInput ? (
                <div
                  style={{ color: '#0076ff', textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => {
                    setShowCustomLabelInput(true);
                  }}
                >
                  {multiFormatMessage("task_custom")}
                </div>
              ) : (
                <>
                  <Input
                    style={{ width: '100%' }}
                    placeholder={multiFormatMessage("task_custom_placeholder")}
                    value={customLabelInputValue}
                    onChange={(value) => {
                      setCustomLabelInputValue(value);
                    }}
                  />
                  <div className="confirm-flex-end" style={{ marginTop: '10px' }}>
                    <Button
                      style={{ minWidth: 50, padding: '0px 15px' }}
                      type="primary"
                      disabled={!customLabelInputValue}
                      onClick={() => {
                        addLabeList(customLabelInputValue, 'label');
                      }}
                    >
                      {commonFormatMessage("confirm")}
                    </Button>
                    <Button
                      style={{ minWidth: 50, padding: '0px 15px' }}
                      type="weak"
                      onClick={() => {
                        setShowCustomLabelInput(false);
                      }}
                    >
                      {commonFormatMessage("return")}
                    </Button>
                  </div>
                </>
              )}
          />
        </Form.Item>
        { /* cycle */}
        <Form.Item
          label={multiFormatMessage('cycle')}
          showStatusIcon={false}
          key={`${currentStep  }cycle`}
        >
          <Select
            className={'dbl-virtual-table-edit-form-input'}
            placeholder={multiFormatMessage('cycle_placeholder')}
            size="full"
            appearance="button"
            searchable={!showCustomCycleInput}
            matchButtonWidth
            value={cycle}
            onChange={(value) => {
              setCycle(value);
            }}
            options={!showCustomCycleInput ? cycleOptions?.map(opt => ({ value: opt.value, text: getText('cycle', opt?.text) })) : []}
            boxClassName={showCustomCycleInput ? 'confirm-model-customize' : ''}
            tips={
              // eslint-disable-next-line no-nested-ternary
              showCustomCycleInput ? (false) : cycleStatus && cycleOptions.length === 0 ? (
                <StatusTip
                  status={cycleStatus}
                  emptyText={commonFormatMessage("no_data")}
                  onRetry={() => {
                    getLabeList();
                  }}
                />
              ) : undefined
            }
            onOpen={() => {
              setShowCustomCycleInput(false);
            }}
          // footer={
          //   !showCustomCycleInput ? (
          //     <div
          //       style={{ color: '#0076ff', textAlign: 'left', cursor: 'pointer' }}
          //       onClick={() => {
          //         setShowCustomCycleInput(true);
          //       }}
          //     >
          //       {multiFormatMessage("task_custom_cycle")}
          //     </div>
          //   ) : (
          //     <>
          //       <Input
          //         style={{ width: '100%' }}
          //         placeholder={multiFormatMessage("task_custom_placeholder")}
          //         value={customCycleInputValue}
          //         onChange={(value) => {
          //           setCustomCycleInputValue(value);
          //         }}
          //       />
          //       <div className="confirm-flex-end" style={{ marginTop: '10px' }}>
          //         <Button
          //           style={{ minWidth: 50, padding: '0px 15px' }}
          //           type="primary"
          //           disabled={!customCycleInputValue}
          //           onClick={() => {
          //             addLabeList(customCycleInputValue, 'cycle');
          //           }}
          //         >
          //           {commonFormatMessage("confirm")}
          //         </Button>
          //         <Button
          //           style={{ minWidth: 50, padding: '0px 15px' }}
          //           type="weak"
          //           onClick={() => {
          //             setShowCustomCycleInput(false);
          //           }}
          //         >
          //           {commonFormatMessage("return")}
          //         </Button>
          //       </div>
          //     </>
          //   )}
          />
        </Form.Item>
        { /* description */}
        <Form.Item
          label={multiFormatMessage('desc')}
          showStatusIcon={false}
        >
          <Input.TextArea
            className={'dbl-virtual-table-edit-form-input-textarea'}
            placeholder={multiFormatMessage('desc_placeholder')}
            value={description}
            onChange={(value) => setDescription(value)}
            maxLength={2560}
            showCount={true}
          />
        </Form.Item>
      </Form>
    );
  }
);

export default VirtualTableEditForm;